import * as _ from 'lodash';

import { LogbookTableColumnsConfiguration } from './LogbookTable.configuration';
import { HeadingDefinition } from './HeadingDefinition';
import { Dictionary, GroupDisplayName, IValueViewModel, NameBuilderConfiguration, TimePeriod } from '../../models';
import { ValueTypes } from '../../common/ValueTypes';
import { aggregationModes, guidEmpty, getUnitName } from '../../common';
import { LogbookTableSeries } from './logbookTable.series';

interface HeadingConfig {
    nameConfiguration?: NameBuilderConfiguration & { hasAggregationMode?: boolean };
    series?: LogbookTableSeries[];
}

function addColumn(column: HeadingDefinition, array: HeadingDefinition[], remainingWidth: number): number {
    column.style = { 'width': column.defaultWidthPercentage + '%' };
    array.push(column);
    remainingWidth -= column.defaultWidthPercentage;
    return remainingWidth;
}

function getSeriesHeadings(
    configuration: HeadingConfig,
    viewModels: IValueViewModel[], 
    map?: Dictionary<string>): string[][] {
    const temp: string[][] = [];
    const { nameConfiguration } = configuration;
    configuration.series
        .sort((a, b) => a.sortOrder > b.sortOrder ? 1 : -1)
        .forEach(s => {
            const searchGuid = s.isBoundToGroup && map && map[s.guid]
                ? map[s.guid]
                : s.guid;
            const found = _.find(viewModels, vm => vm.guid === searchGuid);
            const colContent = [];
            if (nameConfiguration.hasGroupName) {
                colContent.push(found?.groupUniqueName);
            }
            if (nameConfiguration.hasName) {
                colContent.push(found?.name);
            }
            if (nameConfiguration.hasShortComment) {
                colContent.push(found?.shortComment);
            }
            if (nameConfiguration.hasUnit) {
                colContent.push(getUnitName(found?.unitName, s.value));
            }
            if (nameConfiguration.hasValueType) {
                colContent.push(ValueTypes.getValueType(s.value));
            }
            if (nameConfiguration.hasAggregationMode) {
                colContent.push(_.find(aggregationModes, am => am.key === s.aggregationMode)?.value);
            }
            temp.push(colContent);
        });
    return temp;
}

function getFullWidthColumn(row: HeadingDefinition[], remainingWidthColumnSorted: string[]): HeadingDefinition {
    let column: HeadingDefinition = null;
    remainingWidthColumnSorted.forEach(label => {
        if (!column) {
            column = _.find(row, column => column.label === label);
        }
    });
    return column;
}

export function getLogbookTableHeadings(
    configuration: LogbookTableColumnsConfiguration & { series?: LogbookTableSeries[] },
    viewModels: IValueViewModel[],
    map: Dictionary<string>
): HeadingDefinition[][] {
    const categories: HeadingDefinition = { label: 'Categories', defaultWidthPercentage: 20 };
    const dashboardLink: HeadingDefinition = { label: 'Link', defaultWidthPercentage: 5 };
    const dateModified: HeadingDefinition = { label: 'Modified on', defaultWidthPercentage: 20 };
    const description: HeadingDefinition = { label: 'Description', defaultWidthPercentage: 0 };
    const duration: HeadingDefinition = { label: 'Duration (h)', defaultWidthPercentage: 4 };
    const group: HeadingDefinition = { label: 'Group', defaultWidthPercentage: 8 };
    const identifierName: HeadingDefinition = { label: 'Identifier', defaultWidthPercentage: 5 };
    const identifierShortComment: HeadingDefinition = { label: 'Identifier short comment', defaultWidthPercentage: 5 };
    const severity: HeadingDefinition = { label: '', classed: { 'plus-column': true } };
    const timeRange: HeadingDefinition = { label: 'Time period', defaultWidthPercentage: 12 };
    const user: HeadingDefinition = { label: 'User', defaultWidthPercentage: 5 };

    const remainingWidthColumnSorted = ['Description', 'Time period', 'Group', 'Identifier', 'Identifier short comment', 'Categories', 'User', 'Modified on', 'Link', ''];

    const firstRow: HeadingDefinition[] = [];
    const headings = [];

    headings.push(firstRow);
    let remainingWidth = 100;
    if (configuration.hasSeverity) {
        firstRow.push(severity);
    }
    if (configuration.timeRange === TimePeriod.Both) {
        const w = timeRange.defaultWidthPercentage;
        timeRange.style = { 'width': w + '%' };
        firstRow.push(timeRange);
        remainingWidth -= w;
    }
    if (configuration.timeRange === TimePeriod.BeginDate || configuration.timeRange === TimePeriod.EndDate) {
        const w = timeRange.defaultWidthPercentage / 2;
        timeRange.style = { 'width': w + '%' };
        firstRow.push(timeRange);
        remainingWidth -= w;
    }
    if (configuration.hasDuration) {
        remainingWidth = addColumn(duration, firstRow, remainingWidth);
    }
    if (configuration.groupNameDisplayed !== GroupDisplayName.None) {
        remainingWidth = addColumn(group, firstRow, remainingWidth);
    }
    if (configuration.hasIdentifierName) {
        remainingWidth = addColumn(identifierName, firstRow, remainingWidth);
    }
    if (configuration.hasIdentifierShortComment) {
        remainingWidth = addColumn(identifierShortComment, firstRow, remainingWidth);
    }
    if (configuration.hasDescription) {
        remainingWidth = addColumn(description, firstRow, remainingWidth);
    }
    if (configuration.hasCategories) {
        remainingWidth = addColumn(categories, firstRow, remainingWidth);
    }
    if (configuration.hasUser) {
        remainingWidth = addColumn(user, firstRow, remainingWidth);
    }
    if (configuration.hasDateModified) {
        remainingWidth = addColumn(dateModified, firstRow, remainingWidth);
    }
    const headingCount = firstRow.length;
    if (configuration.series?.length) {
        const seriesHeadings = getSeriesHeadings(configuration, viewModels, map);
        const headingRowsCount = seriesHeadings[0].length;
        if (headingRowsCount) {
            const additional = headingRowsCount - 1;
            for (let i = 0; i < additional; i++) {
                const newArray = [];
                for (let j = 0; j < headingCount; j++) {
                    newArray.push({ label: '' });
                }
                headings.push(newArray);
            }
            configuration.series
                .sort((a, b) => a.sortOrder > b.sortOrder ? 1 : -1)
                .forEach((s, i) => {
                    firstRow.push({ label: seriesHeadings[i][0], classed: { 'text-overflow': true } });
                    for (let j = 0; j < additional; j++) {
                        headings[j + 1].splice(headingCount + i, 0, { label: seriesHeadings[i][j + 1], classed: { 'text-overflow': true } });
                    }
                });
            for (let j = 0; j < additional; j++) {
                headings[j + 1].push({ label: '' });
            }
        } else {
            configuration.series
                .sort((a, b) => a.sortOrder > b.sortOrder ? 1 : -1)
                .forEach(() => firstRow.push({ label: '', classed: { 'text-overflow': true } }));
        }
    }
    if (configuration.dashboardGuid && configuration.dashboardGuid !== guidEmpty) {
        remainingWidth = addColumn(dashboardLink, firstRow, remainingWidth);
    }

    const fullWidthColumn: HeadingDefinition = getFullWidthColumn(firstRow, remainingWidthColumnSorted);
    if (fullWidthColumn) {
        fullWidthColumn.style = { 'width': (remainingWidth + fullWidthColumn.defaultWidthPercentage) + '%' };
    }
    return headings;
}